<template>
  <div class="content">
    <section id="the-wall">
      <div
        class="brick"
        v-for="brick in bricks"
        :key="brick.id"
        :class="{ locked: lockedBricks.has(brick.id) }"
        @mouseenter="() => addHoveredBrick(brick.id)"
        @mouseleave="() => removeHoveredBrick(brick.id)"
        @click="() => toggleLockedBrick(brick.id)"
      >
        <img :src="brick.image" :alt="brick.title" />
        <div v-if="lockedBricks.has(brick.id)" class="lock-indicator">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <circle cx="12" cy="7" r="4"></circle>
            <path d="M12 7v4"></path>
          </svg>
        </div>
      </div>
    </section>
  </div>
</template>
<style lang="scss" scoped>
#the-wall {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  padding: 1rem;
  background: var(--color-page-initial);

  @media (max-width: 40rem) {
    grid-template-columns: repeat(2, 1fr);
    padding: 1rem 0;
  }

  .brick {
    position: relative;
    transition: all 0.5s ease;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
    }

    &:hover:not(.locked) {
      background: white;
    }

    &.locked {
      background: #000;
    }

    .lock-indicator {
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      background: black;
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;

      svg {
        width: 12px;
        height: 12px;
      }
    }
  }
}
</style>
<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const wallContext = require.context(
  "@/assets/wall/",
  false,
  /\.(png|jpe?g|gif|svg|webp)$/i
);

// Create initial bricks array
const initialBricks = wallContext.keys().map((key, index) => {
  const filename = key.replace("./", "").replace(/\.[^/.]+$/, "");
  const title = filename
    .replace(/_/g, " ")
    .replace(/\b\w+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

  return {
    id: index + 1,
    image: wallContext(key),
    title: title,
  };
});

// Make bricks reactive so we can shuffle them
const bricks = ref([...initialBricks]);

// Track hovered and locked bricks
const hoveredBricks = ref(new Set());
const lockedBricks = ref(new Set());

const addHoveredBrick = (brickId) => {
  // Don't add hover state if brick is locked
  if (!lockedBricks.value.has(brickId)) {
    hoveredBricks.value.add(brickId);
  }
};

const removeHoveredBrick = (brickId) => {
  hoveredBricks.value.delete(brickId);
};

const toggleLockedBrick = (brickId) => {
  if (lockedBricks.value.has(brickId)) {
    lockedBricks.value.delete(brickId);
  } else {
    lockedBricks.value.add(brickId);
    // Remove from hover state when locked
    hoveredBricks.value.delete(brickId);
  }
};

// Fisher-Yates shuffle algorithm with exclusions
const shuffleArray = (array, excludeIndices = []) => {
  const newArray = [...array];
  const excludeSet = new Set(excludeIndices);

  // Get indices that can be shuffled (not excluded)
  const shuffleableIndices = [];
  for (let i = 0; i < newArray.length; i++) {
    if (!excludeSet.has(i)) {
      shuffleableIndices.push(i);
    }
  }

  // Shuffle only the shuffleable elements
  for (let i = shuffleableIndices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const indexA = shuffleableIndices[i];
    const indexB = shuffleableIndices[j];
    [newArray[indexA], newArray[indexB]] = [newArray[indexB], newArray[indexA]];
  }

  return newArray;
};

const randomizeBricks = () => {
  // Find indices of hovered and locked bricks
  const excludedIndices = [];
  bricks.value.forEach((brick, index) => {
    if (hoveredBricks.value.has(brick.id) || lockedBricks.value.has(brick.id)) {
      excludedIndices.push(index);
    }
  });

  bricks.value = shuffleArray(bricks.value, excludedIndices);
};

// Auto-randomize on mount and set up periodic randomization
let intervalId;

onMounted(() => {
  // Randomize immediately on mount
  randomizeBricks();

  // Set up periodic randomization every second
  intervalId = setInterval(randomizeBricks, 1500);
});

onUnmounted(() => {
  // Clear interval when component is unmounted
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>
